/* eslint-disable react/no-deprecated */

/* eslint class-methods-use-this: 0 */
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import SessionStore from 'stores/SessionStore';
import moment from 'moment';
import DriverStore from 'stores/DriverStore';
import DriverWebAPIUtils, { DOCUMENTS_PER_PAGE } from 'utils/DriverWebAPIUtils';
import Badge from 'components/v2/Badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from 'components/ui/table';
import { getSignedUrl } from 'utils/DocumentsAPIUtils';
import { getFileIconClass } from '../../../../helpers/helper_functions';
import Pagination from 'components/v2/PaginationV2';

class DriverDocuments extends React.Component {
  static propTypes = {
    id: PropTypes.string,
  };

  static defaultProps = {
    id: '',
  };

  constructor(props) {
    super(props);

    const user = SessionStore.getUser();

    this.state = {
      userRole: user._type,
      signedUrls: {},
      documents: [],
      manifestsMeta: {
        total: 0,
        page: 1,
        per_page: DOCUMENTS_PER_PAGE,
        total_pages: 1,
      },
      isLoading: false,
    };
  }

  componentDidMount = () => {
    DriverStore.addChangeListener(this.getManifests);
    this.loadManifests(1);
  };

  componentWillReceiveProps(nextProps) {
    // If the user attempts to switch drivers using the search bar, update manifests...
    if (nextProps && nextProps.id !== this.props.id) {
      this.loadManifests(1, nextProps.id);
    }
  }

  componentWillUnmount() {
    DriverStore.removeChangeListener(this.getManifests);
  }

  /** *************  COMPONENT STATE HANDLERS ******************** */

  getManifests = () => {
    const manifest = DriverStore.getManifest();
    const manifestsMeta = DriverStore.getManifestsMeta();

    this.setState(
      {
        documents: manifest || [],
        manifestsMeta,
        isLoading: false,
      },
      () => {
        if (manifest && manifest.length > 0) {
          this.getSignedUrl();
        }
      },
    );
  };

  loadManifests = (page = 1, driverId = null) => {
    const id = driverId || this.props.id;
    this.setState({ isLoading: true });
    DriverWebAPIUtils.getDriverManifests(id, {
      page,
      per_page: DOCUMENTS_PER_PAGE,
    });
  };

  onPageSelect = (page) => {
    this.loadManifests(page);
  };

  getViewableDocs(docs, initialUserRole) {
    const userRole = this.state.userRole || initialUserRole;
    return docs.filter((doc) => {
      if (doc.hasOwnProperty('show') && doc.show) {
        if (!doc.user_visibility || userRole === 'Admin') {
          return true;
        }
        return doc.user_visibility.indexOf(userRole) > -1;
      }
      return false;
    });
  }

  getSignedUrl = async () => {
    const { t } = this.props;
    // Find document paths
    const docs = this.state.documents;
    const signedUrls = {};
    if (docs.length > 0) {
      try {
        // userRole from above is not in this.state yet, but we need the value to determine visibility of documents
        const viewableDocs = this.getViewableDocs(docs, this.state.userRole);
        const promises = viewableDocs.map(async (d) => {
          const docKey = d.uri || d.path;
          if (docKey) {
            const { data: signedUrl } = await getSignedUrl(docKey);
            return { docKey, signedUrl: signedUrl?.getSignedUrl?.signedUrl };
          }

          console.warn(`${t('DOCUMENT_URI_MISSING', { docName: d.name })}`);
          return { docKey: null, signedUrl: null };
        });

        const results = await Promise.all(promises);
        results.forEach(({ docKey, signedUrl }) => {
          if (docKey && signedUrl) {
            signedUrls[docKey] = signedUrl;
          }
        });
      } catch (e) {
        console.error(t('SHIPMENT_DOCUMENT'), t('ERROR_SIGNED_URL'), e);
      }
    }
    this.setState({ signedUrls });
  };

  /** *************  EVENT HANDLERS ******************** */

  // User Clicks Depart Button
  departManifest = (doc) => {
    if (!doc) {
      return null;
    }

    const { manifestsMeta } = this.state;
    const currentPage = manifestsMeta.page || 1;

    DriverWebAPIUtils.departManifest(doc, {
      onSuccess: () => {
        // Refetch manifests after successful departure to get updated data
        this.loadManifests(currentPage);
      },
    });
  };

  /** *************  VIEW RENDERS  ******************** */

  renderLink(doc) {
    const { t } = this.props;
    const docKey = doc.uri || doc.path;
    const signedUrl = this.state.signedUrls[docKey];
    if (!signedUrl)
      return <span>{t('DOCUMENT_URI_MISSING', { docName: doc.name })}</span>;
    return (
      <a
        className="driver-driverDocuments-viewManifest"
        href={signedUrl}
        target="blank"
      >
        {doc.name}
      </a>
    );
  }

  renderDocuments(docs) {
    const scopedDocs = this.getViewableDocs(docs);
    const { t } = this.props;

    return scopedDocs
      .filter((doc) => !!(doc.uri || doc.path))
      .map((doc, i) => {
        const extension = (doc.uri || doc.path).split('.').pop().toUpperCase();
        const key = `shipment-document-${i}`;
        const timestamp =
          doc.meta && doc.meta.timestamp
            ? moment(doc.meta.timestamp).format('LLLL')
            : '';
        const departed =
          doc.meta && doc.meta.departed ? doc.meta.departed : false;

        // this is pretty hacky but the only alternative is creating a new component for order docs

        const topItemStyle =
          i === 0 && this.props.isOrder
            ? { borderTop: '2px solid transparent' }
            : {};

        return (
          <TableRow style={topItemStyle} key={key}>
            <TableCell>
              <i className={getFileIconClass(extension)} />
            </TableCell>
            <TableCell>{this.renderLink(doc)}</TableCell>
            <TableCell>{timestamp}</TableCell>
            <TableCell>
              {!departed ? (
                <button
                  type="primary"
                  className="button button-green driver-driverDocuments-dispatchDriver"
                  onClick={() => this.departManifest(doc)}
                >
                  {t('DEPART')}
                </button>
              ) : (
                <Badge color="black">{t('DEPARTED')}</Badge>
              )}
            </TableCell>
          </TableRow>
        );
      });
  }

  renderPagination() {
    const { manifestsMeta, isLoading } = this.state;
    const { page, per_page, total } = manifestsMeta;

    if (total <= per_page) return null;

    return (
      <div className="flex justify-center my-2">
        <Pagination
          perPage={per_page}
          page={page}
          total={total}
          onPageClick={this.onPageSelect}
          isLoading={isLoading}
        />
      </div>
    );
  }

  renderDocumentList() {
    const { t } = this.props;
    const { documents, manifestsMeta } = this.state;

    if (manifestsMeta.total === 0) {
      return <i>{t('DRIVER_DOES_NOT_HAVE_DOCUMENTS')}</i>;
    }

    return (
      <div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Document</TableHead>
              <TableHead>Timestamp</TableHead>
              <TableHead>Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>{this.renderDocuments(documents)}</TableBody>
        </Table>
        {this.renderPagination()}
      </div>
    );
  }

  componentDidUpdate(prevProps) {
    // Load manifests if driver ID changes
    if (prevProps.id !== this.props.id) {
      this.loadManifests(1);
    }
  }

  render() {
    return <div>{this.renderDocumentList()}</div>;
  }
}

export default withTranslation()(DriverDocuments);
